import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';
import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { FormQuestion } from 'components/survey/form-questions-list/FormQuestionsList';

/**
 * The application form ("interview") stores itself (auto-save; plan
 * auto-save-solo/attempt/120-interview-plan.md).
 *
 * The screen is small: a list of questions, a logo and two colours. Every
 * question has a name of its own (`ref`), so storing the list twice doubles
 * nothing. Both stores carry the mark of a store the screen makes by itself:
 * on a campaign whose job is open the server then KEEPS it and writes the
 * outside form (what candidates fill in) only at "Publish changes".
 */

/** The logo and the two colours, as they are stored. */
export interface InterviewLook {
  logo_id: number;
  primary_color: string | null;
  secondary_color: string | null;
}

/**
 * The questions as they are stored: without the ones that have no wording
 * yet (the server refuses those), and each name once.
 */
export const questionsToStore = (questions: FormQuestion[]): FormQuestion[] => {
  const order: FormQuestion[] = [];
  const byRef = new Map<string, FormQuestion>();
  questions
    .filter(({ label }) => label !== '')
    .forEach((question) => {
      if (!byRef.has(question.ref)) order.push(question);
      byRef.set(question.ref, question);
    });
  return order.map((question) => byRef.get(question.ref) as FormQuestion);
};

export const questionsPath = (vacancyId: number): string =>
  `/v4/vacancies/${vacancyId}/surveys`;
export const lookPath = (surveyId: number): string =>
  `/v4/vacancy-surveys/${surveyId}`;

export const questionsBody = (
  questions: FormQuestion[]
): Record<string, unknown> => ({
  survey_questions: questions,
  kept_by_itself: true,
});
export const lookBody = (look: InterviewLook): Record<string, unknown> => ({
  ...look,
  kept_by_itself: true,
});

const headers = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
  'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
});

const base = (): string => (import.meta.env.VITE_BASE_URL_API as string) ?? '';

const send = async (
  path: string,
  method: 'POST' | 'PUT',
  body: Record<string, unknown>,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> => {
  const response = await fetch(`${base()}${path}`, {
    method,
    headers: headers(),
    body: JSON.stringify(withBasedOn(body, guard?.basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(
      `The application form was not stored (${response.status}).`
    );
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
  };
  if (answer.success === false) {
    throw new Error('The application form was not stored.');
  }
  guard?.stored(version);
};

/** Stores the questions, quietly. @throws when the server did not confirm */
export const saveQuestions = (
  vacancyId: number,
  questions: FormQuestion[],
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> =>
  send(
    questionsPath(vacancyId),
    'POST',
    questionsBody(questions),
    signal,
    guard
  );

/** Stores the logo and colours, quietly. @throws when the server did not confirm */
export const saveLook = (
  surveyId: number,
  look: InterviewLook,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> =>
  send(lookPath(surveyId), 'PUT', lookBody(look), signal, guard);
